import { useState, useRef, useEffect, useCallback } from "react";

const STORAGE_KEY = "jli-camera-enabled";
const SCAN_INTERVAL_MS = 66; // ~15fps native
const FALLBACK_SCAN_INTERVAL_MS = 100; // ~10fps fallback
const DEDUPE_WINDOW_MS = 1500;
const BARCODE_FORMATS_NATIVE = [
    // 1D barcodes
    "code_128", "code_39", "code_93", "codabar",
    "ean_13", "ean_8", "upc_a", "upc_e", "itf",
    // 2D barcodes
    "qr_code", "data_matrix", "aztec", "pdf417",
];

interface UseCameraBarcodeOptions {
    onScan: (value: string) => void;
    enabled?: boolean;
    // Desired digital zoom level. Defaults to 2.
    zoom?: number;
    // Enable the torch (flashlight). Only used in TNT mode.
    torch?: boolean;
}

interface UseCameraBarcodeReturn {
    isCameraActive: boolean;
    isCameraAvailable: boolean;
    cameraError: string | null;
    toggleCamera: () => void;
    videoRef: React.RefObject<HTMLVideoElement | null>;
}

const useCameraBarcode = ({ onScan, enabled = true, zoom = 2, torch = false }: UseCameraBarcodeOptions): UseCameraBarcodeReturn => {
    const [isCameraActive, setIsCameraActive] = useState(false);
    const [isCameraAvailable, setIsCameraAvailable] = useState(false);
    const [cameraError, setCameraError] = useState<string | null>(null);

    const videoRef = useRef<HTMLVideoElement | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const detectorRef = useRef<any>(null);
    const animFrameRef = useRef<number | null>(null);
    const lastScanTimeRef = useRef(0);
    const lastDetectedRef = useRef({ value: "", timestamp: 0 });
    const onScanRef = useRef(onScan);
    const enabledRef = useRef(enabled);
    const activeRef = useRef(false);
    const modeRef = useRef<"native" | "fallback" | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const decodingRef = useRef(false);
    const Html5QrcodeClassRef = useRef<any>(null);

    useEffect(() => { onScanRef.current = onScan; }, [onScan]);
    useEffect(() => { enabledRef.current = enabled; }, [enabled]);

    useEffect(() => {
        const hasCamera = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
        setIsCameraAvailable(hasCamera);
    }, []);

    const handleDetection = useCallback((value: string) => {
        if (!value || !enabledRef.current) return;
        const now = performance.now();
        const timeSinceLast = now - lastDetectedRef.current.timestamp;

        // Block ALL scans within the cooldown window
        if (timeSinceLast < DEDUPE_WINDOW_MS) return;

        lastDetectedRef.current = { value, timestamp: now };
        onScanRef.current?.(value);
    }, []);

    const detectLoop = useCallback(() => {
        if (!activeRef.current || !videoRef.current) return;

        const video = videoRef.current;
        const interval = modeRef.current === "native" ? SCAN_INTERVAL_MS : FALLBACK_SCAN_INTERVAL_MS;

        // Schedule the next scan at exactly the right interval instead of
        // firing 60 rAF callbacks/sec and discarding most of them.
        animFrameRef.current = setTimeout(detectLoop, interval) as any;

        if (video.readyState < 2 || !enabledRef.current) return;

        if (modeRef.current === "native" && detectorRef.current) {
            detectorRef.current.detect(video)
                .then((barcodes: any[]) => {
                    if (barcodes?.length > 0) {
                        handleDetection(barcodes[0].rawValue);
                    }
                })
                .catch(() => {});
        } else if (modeRef.current === "fallback" && Html5QrcodeClassRef.current) {
            if (decodingRef.current) return;
            decodingRef.current = true;

            const w = video.videoWidth;
            const h = video.videoHeight;
            if (!w || !h) { decodingRef.current = false; return; }

            if (!canvasRef.current) {
                canvasRef.current = document.createElement("canvas");
                canvasRef.current.getContext("2d", { willReadFrequently: true });
            }
            const canvas = canvasRef.current;
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d")!;
            ctx.drawImage(video, 0, 0, w, h);

            canvas.toBlob(async (blob) => {
                if (!blob) { decodingRef.current = false; return; }
                try {
                    const result = await Html5QrcodeClassRef.current.scanFileV2(blob, false);
                    handleDetection(result.decodedText);
                } catch {
                    // No barcode found
                } finally {
                    decodingRef.current = false;
                }
            }, "image/jpeg", 0.85);
        }
    }, [handleDetection]);

    const stopCamera = useCallback(() => {
        activeRef.current = false;
        if (animFrameRef.current) {
            clearTimeout(animFrameRef.current);
            animFrameRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        modeRef.current = null;
        decodingRef.current = false;
        setIsCameraActive(false);
    }, []);

    const startCamera = useCallback(async () => {
        try {
            setCameraError(null);

            const constraints: MediaStreamConstraints = {
                video: {
                    facingMode: { ideal: "environment" },
                    // 1080p balances quality with battery/heat. 4K was causing
                    // phones to overheat from decoding high-res frames at 30fps.
                    width: { ideal: 1920 },
                    height: { ideal: 1080 },
                },
            };

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }

            // Apply autofocus + zoom (best-effort)
            try {
                const track = stream.getVideoTracks()[0];
                const capabilities = (track as any).getCapabilities?.() ?? {};

                // Autofocus
                if (capabilities.focusMode?.includes("continuous")) {
                    try {
                        await track.applyConstraints({ advanced: [{ focusMode: "continuous" } as any] });
                    } catch { /* not supported */ }
                }

                // Zoom — try multiple approaches for maximum device compatibility
                if (capabilities.zoom) {
                    const maxZoom = capabilities.zoom.max || 1;
                    const minZoom = capabilities.zoom.min || 1;
                    const targetZoom = Math.max(minZoom, Math.min(zoom, maxZoom));
                    try {
                        await track.applyConstraints({ advanced: [{ zoom: targetZoom } as any] });
                    } catch {
                        try {
                            await track.applyConstraints({ zoom: targetZoom } as any);
                        } catch { /* zoom not supported on this device */ }
                    }
                }

                // Torch (flashlight) — only enable when requested (TNT mode)
                if (capabilities.torch && torch) {
                    try {
                        await track.applyConstraints({ advanced: [{ torch: true } as any] });
                    } catch { /* torch not supported */ }
                }
            } catch {
                // Capabilities API not supported
            }

            stream.getVideoTracks().forEach(track => {
                track.onended = () => stopCamera();
            });

            // Determine detection mode
            let useNative = false;
            if ("BarcodeDetector" in window) {
                try {
                    const supported = await (window as any).BarcodeDetector.getSupportedFormats();
                    useNative = BARCODE_FORMATS_NATIVE.some((f: string) => supported.includes(f));
                } catch {
                    useNative = true;
                }
            }

            if (useNative) {
                detectorRef.current = new (window as any).BarcodeDetector({
                    formats: BARCODE_FORMATS_NATIVE,
                });
                modeRef.current = "native";
            } else {
                if (!Html5QrcodeClassRef.current) {
                    const { Html5Qrcode } = await import("html5-qrcode");
                    Html5QrcodeClassRef.current = Html5Qrcode;
                }
                modeRef.current = "fallback";
            }

            activeRef.current = true;
            setIsCameraActive(true);

            animFrameRef.current = setTimeout(detectLoop, 0) as any;
        } catch (err: any) {
            if (err.name === "NotAllowedError") {
                setCameraError("permission_denied");
            } else if (err.name === "NotFoundError" || err.name === "OverconstrainedError") {
                setCameraError("not_found");
                setIsCameraAvailable(false);
            } else {
                setCameraError("error");
            }
            setIsCameraActive(false);
        }
    }, [stopCamera, detectLoop]);

    useEffect(() => {
        if (isCameraActive && streamRef.current && videoRef.current) {
            if (videoRef.current.srcObject !== streamRef.current) {
                videoRef.current.srcObject = streamRef.current;
            }
        }
    }, [isCameraActive]);

    // Re-apply zoom and torch when props change (e.g. switching between
    // barcode and TNT mode). No need to restart the camera.
    useEffect(() => {
        if (!isCameraActive || !streamRef.current) return;
        const track = streamRef.current.getVideoTracks()[0];
        if (!track) return;
        const capabilities = (track as any).getCapabilities?.() ?? {};

        if (capabilities.zoom) {
            const minZoom = capabilities.zoom.min || 1;
            const maxZoom = capabilities.zoom.max || 1;
            const targetZoom = Math.max(minZoom, Math.min(zoom, maxZoom));
            track.applyConstraints({ advanced: [{ zoom: targetZoom } as any] })
                .catch(() => track.applyConstraints({ zoom: targetZoom } as any).catch(() => {}));
        }

        if (capabilities.torch) {
            track.applyConstraints({ advanced: [{ torch } as any] })
                .catch(() => {});
        }
    }, [zoom, torch, isCameraActive]);

    const toggleCamera = useCallback(() => {
        if (isCameraActive) {
            stopCamera();
        } else {
            startCamera();
        }
    }, [isCameraActive, stopCamera, startCamera]);

    // Don't stop the camera when disabled — just pause detection
    // The camera feed stays visible so the user can keep scanning after the error clears

    // Camera is always off on page load — user must explicitly turn it on.
    // No localStorage auto-start.

    // When the page becomes visible again (tab switch, screen unlock, navigate
    // back), check if the camera stream died while backgrounded. If so, restart.
    useEffect(() => {
        const handleVisibility = () => {
            if (document.visibilityState !== "visible") return;
            if (!activeRef.current || !streamRef.current) return;

            const track = streamRef.current.getVideoTracks()[0];
            if (!track || track.readyState === "ended") {
                // Stream died while backgrounded — restart
                stopCamera();
                setTimeout(() => startCamera(), 300);
            }
        };

        document.addEventListener("visibilitychange", handleVisibility);
        return () => document.removeEventListener("visibilitychange", handleVisibility);
    }, [stopCamera, startCamera]);

    useEffect(() => {
        return () => {
            activeRef.current = false;
            if (animFrameRef.current) {
                clearTimeout(animFrameRef.current);
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
        };
    }, []);

    return { isCameraActive, isCameraAvailable, cameraError, toggleCamera, videoRef };
};

export default useCameraBarcode;
